import type { WebTask } from '../../../lib/tasks';
import { compareForTaskList, recentTasks } from '../../../lib/agent-task-buckets';
import { visibleTaskTabs, type TabKey, type TaskTab } from './buckets';

// Pure logic behind the one tabbed Tasks renderer (`TasksBody`) — extracted so the tab/filter/ordering
// behaviour is unit-testable without a DOM. Mirrors native's `flatByUpdated` / tab resolution 1:1.

/**
 * The "All" tab's list: every task in the recency window, optionally narrowed to one agent, in the shared
 * reading order — unfinished work above finished, newest-updated within each half. The ordering rule lives
 * in `agent-task-buckets` (`compareForTaskList`) because every surface that lists tasks has to apply the
 * same one; this function only adds the agent filter. `now` injectable for tests.
 */
export function flatByUpdated(tasks: WebTask[], agentId?: string, now: number = Date.now()): WebTask[] {
  return recentTasks(tasks, now)
    .filter((task) => !agentId || task.agentId === agentId)
    .sort((a, b) => compareForTaskList(a, b, now));
}

/**
 * The tab set the renderer shows: the visible status tabs for these tasks.
 *
 * ⚠️ The leading catch-all **All tasks** tab is HIDDEN (product decision, 2026-09-02 — it read as
 * confusing next to the status tabs). Nothing else about it was removed: `TabKey` still carries `'all'`,
 * `flatByUpdated` still builds its list, `TasksBody` still counts it, and `TaskTabs` still knows its
 * label — so re-enabling it is putting `'all'` back at the head of this array (and of `TasksBody`'s
 * `tabs`). Kept in sync with native, which hides its own `TaskTabKey.all` the same way.
 */
export function taskTabKeys(tasks: WebTask[], now: number = Date.now()): TabKey[] {
  // return ['all', ...visibleTaskTabs(tasks, now)];  // ← restores the hidden "All tasks" tab
  return [...visibleTaskTabs(tasks, now)];
}

/**
 * Which tab is active. A user's pick wins (while it's still a rendered tab); otherwise a `?tab=<status>`
 * deep-link selects that status tab if it's visible; otherwise the default is the first NON-EMPTY rendered
 * tab, falling back to the first rendered one.
 *
 * The default used to be the hidden `all` catch-all, which could never be empty. Landing a generic "see
 * all tasks" entry on an empty Action-needed tab while 14 tasks sit one tab over is the regression that
 * rule prevents — it is the same `firstNonEmpty` the buckets module and both phones already apply. The
 * last resort is `tabs[0]` rather than a hard-coded `'waiting'` so hiding (or restoring) a tab can never
 * leave the strip with nothing selected.
 */
export function resolveActiveTab(
  picked: TabKey | null,
  requested: TaskTab | undefined,
  tabs: TabKey[],
  statusTabs: TaskTab[],
  counts: Partial<Record<TabKey, number>> = {},
): TabKey {
  if (picked && tabs.includes(picked)) return picked;
  if (requested && statusTabs.includes(requested)) return requested;
  return tabs.find((tab) => (counts[tab] ?? 0) > 0) ?? tabs[0] ?? 'waiting';
}
